/* eslint-disable react/jsx-wrap-multilines */
/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useCallback, useEffect, useState } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import { Grid } from 'semantic-ui-react';
import { TABLE_ORDER_GROUPS_COLUMNS, TABLE_ORDER_GROUPS_WITH_WAYS_COLUMNS } from './constants';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import {
	getTypesSelector,
	getIsTypesLoadingSelector,
	getGroupsListSelector,
	getIsGroupsListLoadingSelector,
	getModesSelector,
	getIsModesLoadingSelector,
	getWaysListSelector,
	getIsWaysListLoadingSelector,
	getGroupsWithWaysListSelector,
	getIsGroupsWithWaysListLoadingSelector,
} from './selectors';
import {
	getGroupsListAction,
	getGroupsWithWaysListAction,
	getModesAction,
	getTypesAction,
	getWaysListAction,
	resetStateAction,
} from './actions';
import {
	getIsAvailableOrdersGroupsButtonsSelector,
	getIsAvailableOrdersWaysButtonsSelector,
} from '../profile/selectors';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { DataGridComponent } from '../../components/data-grid';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';
import { PageContentWithNav } from '../../components/page-content-with-nav';
import { WaysComponent } from './components/ways';
import { GroupContextComponent } from './components/group-context';
import { CollectionComponent } from './components/collection';
import { getMenuByRules } from '../orders/utils';

const OrderGroupsContainerWrapper = ({
	onGetGroupsList,
	onGetGroupsWithWaysList,
	onGetWaysList,
	groupsList,
	groupsWithWaysList,
	isGroupsListLoading,
	isGroupsWithWaysListLoading,
	onResetState,
	isAvailable,
	onGetTypes,
	types,
	onGetModes,
	modes,
}) => {
	useInjectSaga({ key: 'orderGroupsContainerSaga', saga });
	useInjectReducer({ key: 'orderGroupsContainerReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		onGetGroupsList({ searchQuery: removeModalQuery(query.toString()) });
		onGetGroupsWithWaysList({ searchQuery: removeModalQuery(query.toString()) });
		onGetWaysList();
		onGetTypes();
		onGetModes();
		return () => onResetState();
	}, []);

	const handleAddButtonClick = () => {
		query.delete('id');
		query.delete('tab');
		query.delete('mode');
		query.delete('modal');

		query.set('modal', 'order-group');
		query.set('mode', 'add');

		history.push(`${pathname}?${query}`);
	};

	const handleAddWayButtonClick = () => {
		query.set('modal', 'order-way');
		query.set('mode', 'add');

		history.push(`${pathname}?${query}`);
	};

	const handleSortGroupsChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		onGetGroupsList({ searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterGroupsChange = useCallback(filterModel => {
		const jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_ORDER_GROUPS_COLUMNS(types)));

		query.set('filters', jsonFilters);

		onGetGroupsList({ searchQuery: removeModalQuery(query.toString()) });
	});

	const handleSortWaysChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		onGetGroupsWithWaysList({ searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterWaysChange = useCallback(filterModel => {
		const jsonFilters = JSON.stringify(
			normalizeDataGridModel(filterModel, TABLE_ORDER_GROUPS_WITH_WAYS_COLUMNS(types)),
		);

		query.set('filters', jsonFilters);

		onGetGroupsWithWaysList({ searchQuery: removeModalQuery(query.toString()) });
	});

	const getDetailCollectionGroupPanelContent = useCallback(row => {
		return <CollectionComponent row={row.row} modes={modes} type="group" />;
	});

	const getDetailCollectionWayPanelContent = useCallback(row => {
		return <CollectionComponent row={row.row} modes={modes} type="way" />;
	});

	const getDetailWaysGroupPanelContent = useCallback(row => {
		return <WaysComponent row={row.row} getDetailCollectionWayPanelContent={getDetailCollectionWayPanelContent} />;
	});

	const [contextMenu, setContextMenu] = useState(null);

	const [selectedRow, setSelectedRow] = useState(null);

	const handleContextMenu = event => {
		event.preventDefault();

		const rowElement = event.target.closest('.MuiDataGrid-row');

		if (rowElement) {
			const rowId = rowElement.getAttribute('data-id');

			const row = groupsList.find(r => `${r.uid}` === `${rowId}`);

			setSelectedRow(row);
			setContextMenu({
				mouseX: event.clientX + 2,
				mouseY: event.clientY - 6,
			});
		}
	};

	const contextComponent = (
		<GroupContextComponent
			contextMenu={contextMenu}
			setContextMenu={setContextMenu}
			selectedRows={[]}
			group={selectedRow}
		/>
	);

	const [contextMenuWithWays, setContextMenuWithWays] = useState(null);

	const [selectedRowWithWays, setSelectedRowWithWays] = useState(null);

	const handleContextWithWaysMenu = event => {
		event.preventDefault();

		const rowElement = event.target.closest('.MuiDataGrid-row');

		if (rowElement) {
			const rowId = rowElement.getAttribute('data-id');

			const row = groupsWithWaysList.find(r => `${r.uid}` === `${rowId}`);

			setSelectedRowWithWays(row);
			setContextMenuWithWays({
				mouseX: event.clientX + 2,
				mouseY: event.clientY - 6,
			});
		}
	};

	const contextWithWaysComponent = (
		<GroupContextComponent
			contextMenu={contextMenuWithWays}
			setContextMenu={setContextMenuWithWays}
			selectedRows={[]}
			group={selectedRowWithWays}
			isWithWays
		/>
	);

	return (
		<PageContentWithNav
			pageNavigationItems={getMenuByRules()}
			isInnerRouter
			pageContent={
				<Grid>
					<Grid.Column width={8}>
						<Grid.Row>
							<DataGridComponent
								keyTable="group"
								headerText="Группировка заказов"
								onAddButtonClick={isAvailable.create ? handleAddButtonClick : null}
								columns={TABLE_ORDER_GROUPS_COLUMNS(types)}
								rows={groupsList}
								isTableDataLoading={isGroupsListLoading}
								handleSortChange={handleSortGroupsChange}
								handleFilterChange={handleFilterGroupsChange}
								getDetailPanelContent={getDetailCollectionGroupPanelContent}
								handleContextMenu={handleContextMenu}
								contextComponent={contextComponent}
								detailPanelHeight={620}
								getRowId={row => row.uid}
							/>
						</Grid.Row>
					</Grid.Column>
					<Grid.Column width={8}>
						<Grid.Row>
							<DataGridComponent
								keyTable="way"
								headerText="Маршруты"
								onAddButtonClick={isAvailable.create ? handleAddWayButtonClick : null}
								columns={TABLE_ORDER_GROUPS_WITH_WAYS_COLUMNS(types)}
								rows={groupsWithWaysList}
								isTableDataLoading={isGroupsWithWaysListLoading}
								handleSortChange={handleSortWaysChange}
								handleFilterChange={handleFilterWaysChange}
								getDetailPanelContent={getDetailWaysGroupPanelContent}
								handleContextMenu={handleContextWithWaysMenu}
								contextComponent={contextWithWaysComponent}
								detailPanelHeight={620}
								getRowId={row => row.uid}
							/>
						</Grid.Row>
					</Grid.Column>
				</Grid>
			}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	groupsList: getGroupsListSelector(),
	groupsWithWaysList: getGroupsWithWaysListSelector(),
	waysList: getWaysListSelector(),
	isGroupsListLoading: getIsGroupsListLoadingSelector(),
	isGroupsWithWaysListLoading: getIsGroupsWithWaysListLoadingSelector(),
	isWaysListLoading: getIsWaysListLoadingSelector(),
	types: getTypesSelector(),
	isTypesLoading: getIsTypesLoadingSelector(),
	isAvailable: getIsAvailableOrdersGroupsButtonsSelector(),
	isAvailableWays: getIsAvailableOrdersWaysButtonsSelector(),
	modes: getModesSelector(),
	isModesLoading: getIsModesLoadingSelector(),
});

const mapDispatchToProps = {
	onGetGroupsList: getGroupsListAction,
	onGetGroupsWithWaysList: getGroupsWithWaysListAction,
	onGetWaysList: getWaysListAction,
	onGetTypes: getTypesAction,
	onGetModes: getModesAction,
	onResetState: resetStateAction,
};

const OrderGroupsContainer = connect(mapStateToProps, mapDispatchToProps)(OrderGroupsContainerWrapper);

OrderGroupsContainerWrapper.propTypes = {
	onGetGroupsList: PropTypes.func.isRequired,
	onGetGroupsWithWaysList: PropTypes.func.isRequired,
	onGetWaysList: PropTypes.func.isRequired,
	groupsList: PropTypes.arrayOf(PropTypes.object).isRequired,
	groupsWithWaysList: PropTypes.arrayOf(PropTypes.object).isRequired,
	waysList: PropTypes.arrayOf(PropTypes.object).isRequired,
	isGroupsListLoading: PropTypes.bool.isRequired,
	isGroupsWithWaysListLoading: PropTypes.bool.isRequired,
	isWaysListLoading: PropTypes.bool.isRequired,
	onGetTypes: PropTypes.func.isRequired,
	types: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string.isRequired,
		}),
	),
	isTypesLoading: PropTypes.bool.isRequired,
	onGetModes: PropTypes.func.isRequired,
	modes: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string.isRequired,
		}),
	),
	isModesLoading: PropTypes.bool.isRequired,
	onResetState: PropTypes.func,
	isAvailable: PropTypes.objectOf(PropTypes.bool),
	isAvailableWays: PropTypes.objectOf(PropTypes.bool),
};

export default OrderGroupsContainer;
